import { FC, useCallback, useState } from 'react';

import { MenuPlacement } from 'react-select';

import {
  BasicAddressFragment,
  useGetAddressLazyQuery,
} from '@graphql/requests';

import { useFetchAddressSuggestions } from '@ui-furniture/common/form/address/use-fetch-address-suggestions';
import {
  InputBorder,
  Label,
  LabelText,
} from '@ui-furniture/common/form/common';
import { Typeahead } from '@ui-furniture/common/react-select';
import { Text } from '@ui-furniture/elements';

export const AddressTypeahead: FC<{
  label?: string;
  isOptional?: boolean;
  informationTooltip?: JSX.Element;
  menuPlacement?: MenuPlacement;
  onSelect: (
    answer: { text: string } & Omit<BasicAddressFragment, '__typename'>
  ) => void;
}> = ({
  menuPlacement,
  onSelect,
  label,
  informationTooltip,
  isOptional = false,
}) => {
  const [text, setText] = useState('');
  const [getAddress] = useGetAddressLazyQuery();
  const fetchAddressSuggestions = useFetchAddressSuggestions();
  const onChange = useCallback(
    async ({ value: addressId }: { label: string; value: string }) => {
      const response = await getAddress({
        variables: {
          id: addressId,
        },
      });
      const address = response.data?.address;
      if (!address) throw new Error('Address not found');
      const formattedAddress = `${address.line1}, ${address.postcode}`;
      setText(formattedAddress);
      onSelect({
        text: formattedAddress,
        ...address,
      });
    },
    []
  );

  return (
    <Label w='100%'>
      {label ? (
        <LabelText
          isOptional={isOptional}
          informationTooltip={informationTooltip}
        >
          {label}
        </LabelText>
      ) : null}
      <InputBorder flex='1' textAlign={'left'}>
        <Typeahead
          cacheOptions
          loadOptions={fetchAddressSuggestions}
          onChange={onChange}
          inputValue={text}
          setInputValue={setText}
          noOptionsMessage={() => (
            <Text>Sorry, we can’t find that address. Please try again.</Text>
          )}
          menuPlacement={menuPlacement}
          placeholder='What’s the address?'
          menuIsOpen
          styles={{
            menu: {
              maxWidth: '100%',
            },
          }}
        />
      </InputBorder>
    </Label>
  );
};
